import {
  checkFavorites,
  setFavorites,
} from '@/pages/opinion/services/favorites';
import { getBasePath } from '@/utils/utils';
import { debounce } from 'lodash-es';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { urlMapToFilterParams } from '../constants';
import { objToUrl, urlToMap } from '@/pages/opinion/utils/url';
import { useFilterDataHook } from '@/pages/opinion/components/FilterCommon';
import { FilterKeys } from '@/pages/opinion/components/FilterCommon/types';
import {
  deviationKeys,
  filterKeyMapValueKey,
} from '@/pages/opinion/components/FilterCommon/constants/filterOptions';
import {
  notificationError,
  notificationSuccess,
} from '@/pages/opinion/pages/report/gpt/constant';
import { useIntl } from 'umi';
export function useReportName({ gameInfo, id, report_name = '' }: any) {
  const [reportName, setReportName] = useState(report_name);
  const [errorText, setErrorText] = useState('');
  const disable = useMemo(() => {
    return !reportName.trim() || !!errorText;
  }, [reportName, errorText]);

  const checkFavoritesValueFunc = (value: string) => {
    if (!value.trim()) return;
    // if (/[&<>@.。、，,]/.test(value)) {
    //   return setErrorText('Only Chinese, English and numbers can be input.');
    // }
    const params: any = {
      edition_unified_id: gameInfo.edition_unified_id,
      report_name: value.trim(),
    };
    if (id) {
      params.id = id;
    }
    checkFavorites(params).then((data: any) => {
      if (!data) {
        setErrorText('');
      } else {
        setErrorText('A page with the same name already exists.');
      }
    });
  };

  const checkFavoritesValue = debounce((value: string) => {
    checkFavoritesValueFunc(value);
  }, 500);

  useEffect(() => {
    checkFavoritesValueFunc(reportName);
  }, []);

  return { reportName, checkFavoritesValue, disable, errorText, setReportName };
}

export function useSubmitReport({
  pagePath,
  gameInfo,
  addCallback,
  defaultSelectedFilter,
  id,
  editInfo,
}: any) {
  const [systemValue, setSystemValue] = useState<any>('opinions');
  const { formatMessage } = useIntl();
  const originalUrl = useMemo(() => {
    let path = pagePath || '';
    const map = urlToMap(path);
    map.set('gameid', gameInfo.edition_unified_id);
    let urlObj: any = {};
    map.forEach((v, k) => {
      urlObj[k] = v;
    });
    let params = objToUrl(urlObj);
    return `${location.origin}${getBasePath()}${path.split('?')[0]}?${params}`;
  }, [gameInfo]);

  // TODO 这里曝露出去一个filterDefaultConfig
  const { filterOptionsParams, pageImage, filterParams } = useMemo(() => {
    const { filterOptionsParams, pageImage, defaultConfig, ...filterParams } =
      urlMapToFilterParams[pagePath] || {};
    const newDefaultConfig = Object.assign({}, defaultConfig || {}, {
      regionAndLanguage: { toggleLanguage: false },
      region: { showTips: false },
    });
    return {
      pageImage,
      filterOptionsParams,
      filterParams: { ...filterParams, defaultConfig: newDefaultConfig },
    };
  }, [pagePath]);

  const { filterOptions } = useFilterDataHook({
    ...(filterOptionsParams || {}),
    gameInfo: gameInfo,
  });

  const img = useMemo(() => {
    return typeof pageImage === 'string'
      ? pageImage
      : pageImage?.[gameInfo.entity_type as 'pc' | 'mobile' | 'console'];
  }, [gameInfo]);

  const realFilterOptions = useMemo(() => {
    let filter = filterOptions.filter(
      (item) => !['date', 'reviewers', 'liveVenues', 'steamIds'].includes(item),
    );

    if (filter.includes('regionAndLanguage')) {
      filter = filter.filter((item) => !['region', 'language'].includes(item));
    }

    return filter;
  }, [filterOptions]);

  const [selectedFilterOptions, setSelectedFilterOptions] = useState<
    Array<FilterKeys>
  >(defaultSelectedFilter || realFilterOptions);

  const getSelectedFilterData = useCallback(
    (filterValue: any) => {
      let info: any = {};
      let keys = selectedFilterOptions.reduce(
        (pre: Array<string>, cur: FilterKeys) => {
          const valueKeys = filterKeyMapValueKey[cur] || [];
          const value = Array.isArray(valueKeys) ? valueKeys : [valueKeys];
          return [...pre, ...value];
        },
        [],
      );

      keys.forEach((key) => {
        const dataKey = deviationKeys[key] || key;
        if (filterValue?.[dataKey]) {
          info[key] = filterValue[dataKey];
        }
      });

      // let cache = beforeCacheDealwithData(info);
      return info;
    },
    [selectedFilterOptions],
  );

  const submit = async (
    dealwithBackData: (
      noCache: boolean,
      isSave: boolean,
    ) => { value: {}; fixedHeader: {} },
    reportName: string,
    close: () => void,
  ) => {
    if (!reportName) return;
    const filterValue = dealwithBackData(true, true);
    const value = getSelectedFilterData(filterValue.value);

    const filterSetting = JSON.stringify({
      filter: value,
      selectedFilter: selectedFilterOptions,
    });
    const params: any = {
      edition_unified_id: gameInfo.edition_unified_id,
      report_name: reportName,
      router_path: pagePath,
      filter_settings: filterSetting,
    };
    if (id) {
      params.id = id;
      params.sort = Number(editInfo.sort);
    }
    setFavorites(params)
      .then((data: any) => {
        addCallback?.({
          id: data.id || id,
          name_en: reportName,
          name_cn: reportName,
          assembly_url: pagePath,
          filter_settings: filterSetting,
        });
        close();
        notificationSuccess(
          formatMessage({
            id: 'The page has been successfully added to the Favorite Page.',
          }),
        );
      })
      .catch(() => {
        notificationError(
          formatMessage({
            id: 'Addition failed.',
          }),
        );
      });
  };

  const optionsHeight = useMemo(() => {
    if (realFilterOptions.length === 0) return 0;
    let num = 0;
    if (realFilterOptions.includes('regionAndLanguage')) num++;
    if (realFilterOptions.includes('keyWords')) num++;
    if (realFilterOptions.includes('monitorSource')) num++;
    num += Math.ceil((realFilterOptions.length - num) / 2);
    return num * 56 - 10;
  }, [realFilterOptions]);
  return {
    submit,
    filterOptions: realFilterOptions,
    selectedFilterOptions,
    setSelectedFilterOptions,
    originalUrl,
    pageImage: img,
    systemValue,
    setSystemValue,
    filterParams,
    optionsHeight,
  };
}
